import { RedemptionType } from '@/api/model/redemptionType';
import { VoucherStatus } from '@/api/model/voucherStatus';
import { VoucherCreateForm } from '@/components/vouchers/VoucherCreateForm';
import { ImageUrlsField } from '@/components/ui/image-urls-field';
import type { VoucherFormData } from '@/components/vouchers/VoucherForm';
import { VoucherPreview } from '@/components/vouchers/VoucherPreview';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import React, { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Link } from 'react-router';

export const VoucherCreatePage: React.FC = () => {
  const [previewData, setPreviewData] = useState<VoucherFormData>({
    title: '',
    provider_id: '',
    description: '',
    conditions: '',
    value: 0,
    valid_from: '',
    valid_until: '',
    max_redemptions: undefined,
    status: VoucherStatus.DRAFT,
    redemption_type: RedemptionType.CODE,
    images: [],
  });
  const [formInstance, setFormInstance] = useState<UseFormReturn<VoucherFormData> | null>(null);

  return (
    <div className='space-y-6'>
      {/* Page Header */}
      <div className='flex flex-col gap-2'>
        <div className='flex items-center gap-4'>
          <Link to='/admin/vouchers'>
            <ArrowLeft className='h-6 w-6 text-swd-tuerkis-dark' />
          </Link>

          <h1 className='text-3xl leading-9 font-semibold text-swd-tuerkis-darker'>Neuen Gutschein erstellen</h1>
        </div>
        <p className='text-base leading-6 font-medium tracking-wide text-neutral-grau'>
          Erstellen Sie einen neuen Gutschein für Ihre Kunden mit allen erforderlichen Details.
        </p>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Form Section */}
        <div className='lg:col-span-2'>
          <VoucherCreateForm onFormDataChange={setPreviewData} hideImagesField onFormInstance={setFormInstance} />
        </div>

        {/* Preview Section */}
        <div className='lg:sticky lg:top-6 lg:self-start lg:col-span-1 space-y-6'>
          <VoucherPreview formData={previewData} />
          {formInstance && (
            <>
              <Form {...formInstance}>
                <ImageUrlsField
                  control={formInstance.control}
                  name={'images'}
                  label='Bild'
                  description='Bild-URLs für diesen Gutschein hinzufügen'
                  maxImages={5}
                />
              </Form>
              <Form {...formInstance}>
                <Card className='bg-white border border-neutral-hellgrau rounded-xl'>
                  <CardHeader>
                    <CardTitle>Status</CardTitle>
                  </CardHeader>
                  <CardContent className='space-y-4'>
                    <FormField
                      control={formInstance.control}
                      name='status'
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className='text-sm font-bold tracking-wide text-neutral-dunkelgrau'>
                            Status
                          </FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className='h-9 rounded-xl border-[1.5px] border-neutral-mittelgrau bg-white text-neutral-dunkelgrau text-sm font-medium w-full'>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value={VoucherStatus.DRAFT}>Entwurf</SelectItem>
                              <SelectItem value={VoucherStatus.PUBLISHED}>Veröffentlicht</SelectItem>
                              <SelectItem value={VoucherStatus.ARCHIVED}>Archiviert</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              </Form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
